"use client";

import { useTranslations } from "next-intl";
import {
  Paper,
  Text,
  Stack,
  Grid,
  TbCoinEuro,
  TbArchive,
  Button,
  Tabs,
  Avatar,
  DataTable,
  Collapsible,
  StatCard,
  TbHomeEco,
  Badge,
  Box,
  TbExternalLink,
} from "@forestsync/ui";
import { TbEmpathize, TbTarget, TbCalendar } from "@forestsync/ui";
import Link from "next/link";
import type { FC } from "react";
import type { BeneficiaryDetail } from "@/types/glassroom";
import { getOrganizationTypeBadge } from "@forestsync/lib/type-utils";
import { getTransactionTypeDisplayName } from "@forestsync/lib/type-utils";

interface BeneficiaryDetailViewProps {
  user: BeneficiaryDetail & { hasBothRoles?: boolean };
}

export const BeneficiaryDetailView: FC<BeneficiaryDetailViewProps> = ({
  user,
}) => {
  const t = useTranslations();

  // Get organization type badge
  const orgType = getOrganizationTypeBadge(user.type || 0, t);

  // Format date helper
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  // Format transaction type
  const formatTransactionType = (tx: any) =>
    getTransactionTypeDisplayName(tx, user.refId || "", t);

  // Group purchase history by year
  const purchaseHistoryByYear =
    user.purchaseHistory?.reduce(
      (acc, purchase) => {
        const year = new Date(purchase.timestamp).getFullYear().toString();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(purchase);
        return acc;
      },
      {} as Record<string, typeof user.purchaseHistory>,
    ) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <TbEmpathize size={30} className="text-blue-700 dark:text-blue-300" />
          <div>
            <Text
              size="xl"
              component="span"
              fw={700}
              className="text-gray-900 dark:text-white"
            >
              {user.name}{" "}
              <Badge variant="light" color={orgType.color} className="ml-2">
                {orgType.label}
              </Badge>
            </Text>
            <Text size="sm" className="text-gray-500 dark:text-gray-400">
              {[user.municipality, user.country].filter(Boolean).join(", ")}
            </Text>
          </div>
        </div>
        {/* Switch Role Button */}
        {user.hasBothRoles && (
          <Button
            component={Link}
            href={`/developer/${user.refId}`}
            color="timber"
            size="md"
            leftSection={<TbHomeEco size={16} />}
          >
            {t("glassroom.viewProjectDeveloperProfile")}
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("glassroom.table.creditsPurchased")}
            value={user.totalOffsetsPurchased ?? 0}
            icon={TbCoinEuro}
            color="blue"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("glassroom.table.creditsRetired")}
            value={user.totalOffsetsSpent ?? 0}
            icon={TbArchive}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <StatCard
            title={t("glassroom.table.activeCredits")}
            value={user.totalActiveCredits ?? 0}
            icon={TbTarget}
            color="emerald"
          />
        </Grid.Col>
      </Grid>

      {/* Overview and Contact Card Row */}
      <Grid align="stretch">
        {/* Overview */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Paper withBorder p="lg" radius="md" h="100%">
            <Stack gap="md">
              {user.description && (
                <div>
                  <Text
                    size="sm"
                    fw={600}
                    className="text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t("common.description")}
                  </Text>
                  <Text size="sm" className="text-gray-600 dark:text-gray-400">
                    {user.description}
                  </Text>
                </div>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
        {/* Contact Card */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Paper
            withBorder
            p="lg"
            radius="md"
            className="flex flex-col items-center h-full"
          >
            <Avatar
              size={150}
              radius="50%"
              src={user.logoUrl ?? undefined}
              name={user.contactInfo?.name ?? user.name ?? undefined}
              className="mb-3"
              styles={{ image: { objectFit: "contain", padding: 6 } }}
            />
            <Text fw={600} className="mb-1 text-center">
              {user.contactInfo?.name || user.name}
            </Text>
            {user.contactInfo?.email && (
              <Text size="sm" className="text-gray-600 mb-1 text-center">
                {user.contactInfo.email}
              </Text>
            )}
            {user.contactInfo?.website && (
              <Text size="sm" className="text-blue-600 mb-1 text-center">
                {user.contactInfo.website}
              </Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Tabs Section */}
      <Paper withBorder p={{ base: "xs", sm: "lg" }} radius="md">
        <Tabs defaultValue="purchaseHistory">
          <Tabs.List>
            <Tabs.Tab value="purchaseHistory">
              {t("glassroom.table.purchaseHistory")}
            </Tabs.Tab>
            <Tabs.Tab value="co2Retired">
              {t("glassroom.table.co2Retired")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="purchaseHistory" pt="md">
            <div className="space-y-2">
              {Object.entries(purchaseHistoryByYear)
                .sort(([a], [b]) => parseInt(b) - parseInt(a))
                .map(([year, purchases]) => (
                  <Collapsible
                    key={year}
                    label={year}
                    count={purchases.length}
                    icon={<TbCalendar size={18} className="text-blue-500" />}
                    depth={0}
                    color="blue"
                  >
                    <Box pl={{ base: "xs", sm: "md" }}>
                      <DataTable
                        data={purchases.map((purchase) => ({
                          id: purchase.refId, // Use refId as the unique identifier
                          refId: purchase.refId,
                          date: formatDate(purchase.timestamp),
                          type: formatTransactionType(purchase),
                          amount: purchase.totalTokens.toLocaleString(),
                          fromUser: purchase.fromUserName || "N/A",
                          price: purchase.pricePerTon
                            ? `€${purchase.pricePerTon.toFixed(2)}`
                            : "N/A",
                        }))}
                        columns={[
                          { key: "date", label: t("glassroom.table.date") },
                          { key: "type", label: t("glassroom.table.type") },
                          {
                            key: "amount",
                            label: t("glassroom.table.amount"),
                          },
                          { key: "fromUser", label: "From" },
                          { key: "price", label: t("glassroom.table.price") },
                          {
                            key: "actions",
                            label: t("glassroom.table.actions"),
                            render: (value: any, row: any) => (
                              <Button
                                component={Link}
                                href={`/transaction/${row.refId}`}
                                size="xs"
                                variant="light"
                                leftSection={<TbExternalLink size={12} />}
                              >
                                {t("transaction.viewTransaction")}
                              </Button>
                            ),
                          },
                        ]}
                        itemsPerPage={10}
                        compact={true}
                        emptyMessage={t("glassroom.table.noData")}
                      />
                    </Box>
                  </Collapsible>
                ))}
            </div>
          </Tabs.Panel>

          <Tabs.Panel value="co2Retired" pt="md">
            <DataTable
              data={
                user.retirementHistory?.map((retirement) => ({
                  id: retirement.refId, // Use refId as the unique identifier
                  refId: retirement.refId,
                  date: formatDate(retirement.timestamp),
                  amount: retirement.totalTokens.toLocaleString(),
                  purpose: retirement.reason || "Carbon offset retirement",
                  price: retirement.pricePerTon
                    ? `€${retirement.pricePerTon.toFixed(2)}`
                    : "N/A",
                  totalValue: retirement.pricePerTon
                    ? `€${(retirement.pricePerTon * retirement.totalTokens).toFixed(2)}`
                    : "N/A",
                })) || []
              }
              columns={[
                { key: "date", label: t("glassroom.table.date") },
                { key: "amount", label: "Amount (tCO₂e)" },
                { key: "purpose", label: "Purpose" },
                { key: "price", label: "Price/ton" },
                { key: "totalValue", label: "Total Value" },
                {
                  key: "actions",
                  label: t("glassroom.table.actions"),
                  render: (value: any, row: any) => (
                    <Button
                      component={Link}
                      href={`/transaction/${row.refId}`}
                      size="xs"
                      variant="light"
                      leftSection={<TbExternalLink size={12} />}
                    >
                      {t("transaction.viewTransaction")}
                    </Button>
                  ),
                },
              ]}
              itemsPerPage={10}
              compact={true}
              emptyMessage={t("glassroom.table.noData")}
            />
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </div>
  );
};
